export type OnboardingStepId = "resume" | "network" | "goal" | "review";

export const onboardingStepOrder = [
  "resume",
  "network",
  "goal",
  "review",
] as const satisfies readonly OnboardingStepId[];

export type OnboardingDraftStatus =
  | "not_started"
  | "in_progress"
  | "ready_to_complete"
  | "complete";

export type ResumeUploadStatus = "empty" | "uploaded" | "parsed";

export type OnboardingResumeSource =
  | "quick_chat"
  | "resume_upload"
  | "linkedin_screenshot";

export interface ResumeParsedSignal {
  id: string;
  label: string;
  value: string;
}

export interface OnboardingParsedResumeExperience {
  role: string;
  company: string;
  dates?: string;
  location?: string;
}

export interface OnboardingParsedResume {
  fullName: string;
  email: string;
  location: string;
  university: string;
  graduationYear: string;
  major: string;
  minor: string;
  skills: string[];
  experiences: OnboardingParsedResumeExperience[];
  values: string[];
  focusAreas: string[];
  suggestedTargetRoles: string[];
  suggestedTargetIndustries: string[];
  summary: string;
  // Optional, additive. Populated by LinkedIn screenshot extraction; left
  // undefined for resume/quick-chat parses so existing displays are unchanged.
  headline?: string;
  educationList?: string[];
}

export interface OnboardingQuickChatResume {
  firstName: string;
  currentRoleCompany: string;
  tenure: string;
  previousWork: string;
}

export interface OnboardingResume {
  fileName: string;
  fileSizeLabel: string;
  uploadedAt: string | null;
  status: ResumeUploadStatus;
  parsedSignals: ResumeParsedSignal[];
  source?: OnboardingResumeSource;
  parsedCv?: OnboardingParsedResume;
  quickChat?: OnboardingQuickChatResume;
}

export type CareerGoalKind =
  | "find_next_role"
  | "change_function"
  | "build_network"
  | "liminl_decide"
  | "other";

export type GoalTimeframe =
  | "now"
  | "one_to_three_months"
  | "three_to_six_months"
  | "six_months_plus"
  | "exploratory";

export type WorkModePreference = "remote" | "hybrid" | "onsite" | "open";

export type SearchTrack = "internship" | "full_time" | "grad_school_prep" | "exploration";

export type OnboardingTargetKind = "role" | "company" | "industry" | "domain";
export type OnboardingTargetPriority = "primary" | "secondary" | "watch";

export interface OnboardingTarget {
  id: string;
  kind: OnboardingTargetKind;
  label: string;
  priority: OnboardingTargetPriority;
  roleTitle?: string;
  companyName?: string;
  industryName?: string;
  domainName?: string;
  reason?: string;
  tags: string[];
}

export type WorkAuthorizationStatus =
  | "citizen"
  | "permanent_resident"
  | "authorized"
  | "needs_sponsorship"
  | "other";

export interface OnboardingWorkAuthorization {
  status?: WorkAuthorizationStatus;
  needsSponsorship?: boolean;
  details?: string | null;
}

export type OnboardingSeniorityBand = "PEER" | "MID" | "SENIOR";

export interface OnboardingGoal {
  kind: CareerGoalKind;
  headline: string;
  targetOutcome: string;
  timeframe: GoalTimeframe;
  searchTrack: SearchTrack;
  locations: string[];
  workMode: WorkModePreference;
  targets: OnboardingTarget[];
  notes?: string;
  // Structured career preferences (optional, additive). Captured by the
  // career_preferences chat stage and mapped to the goals API wire fields.
  salaryMin?: number;
  salaryMax?: number;
  salaryCurrency?: "USD";
  salaryPeriod?: "year" | "hour";
  workAuthorization?: OnboardingWorkAuthorization;
  /** Must haves → UserGoals.focus_areas */
  focusAreas?: string[];
  /** Job scoring seniority → SearchCriteria.seniorityBand */
  seniorityBand?: OnboardingSeniorityBand;
}

export type NetworkSourceKind =
  | "google"
  | "linkedin"
  | "csv_import"
  | "manual_contacts";

export type NetworkSourceIntent = "connect_now" | "connect_later" | "skip";
export type NetworkActionStatus = "idle" | "queued" | "uploaded" | "added";
export type NetworkPermissionScope =
  | "contacts"
  | "email"
  | "calendar"
  | "profile"
  | "documents";

export interface OnboardingNetworkSource {
  kind: NetworkSourceKind;
  label: string;
  intent: NetworkSourceIntent;
  connected: boolean;
  actionStatus: NetworkActionStatus;
  permissions: NetworkPermissionScope[];
  uploadedFileName?: string;
  estimatedContacts?: number;
  addedCount?: number;
  notes?: string;
}

export interface OnboardingManualContact {
  id: string;
  fullName: string;
  role?: string;
  company?: string;
  email?: string;
  linkedinUrl?: string;
  relationship?: string;
  relationshipContext?: string;
  notes?: string;
  inviteChannel?: "text" | "email";
  inviteStatus?: "drafted" | "sent" | "skipped" | "needs_contact";
  inviteMessage?: string;
  inviteUrl?: string;
  trustedCircleMemberId?: string;
  inviteId?: string;
}

export type OutreachComfort =
  | "warm_intro_only"
  | "known_contacts_first"
  | "draft_before_sending"
  | "open_to_broader_outreach";

export type IntroPreference = "ask_for_context" | "ask_for_intro" | "direct_message" | "draft_only";

export interface RelationshipBoundary {
  id: string;
  label: string;
  enabled: boolean;
}

export interface RelationshipBoundaryTemplate {
  id: string;
  label: string;
}

export interface OnboardingRelationshipComfort {
  outreachComfort: OutreachComfort;
  introPreference: IntroPreference;
  allowDraftGeneration: boolean;
  boundaries: RelationshipBoundary[];
  notes?: string;
}

export interface OnboardingReview {
  acceptedAssumptions: string[];
  editsNeeded: string[];
  confirmedAt: string | null;
}

export interface OnboardingPhoneVerification {
  displayNumber: string;
  e164: string;
  verified: boolean;
  verifiedAt: string | null;
  verificationSource: "api" | "fallback";
}

export type OnboardingCareerQuizSource = "generated" | "fallback";

export interface OnboardingCareerQuizOption {
  id: string;
  label: string;
  scoreKey: string;
  scores?: Record<string, number>;
}

export interface OnboardingCareerQuizQuestion {
  id: string;
  text: string;
  options: OnboardingCareerQuizOption[];
}

export interface OnboardingCareerQuizAnswer {
  questionId: string;
  optionId: string;
  label: string;
  scoreKey: string;
  scores?: Record<string, number>;
}

export interface OnboardingCareerPathPreview {
  id: string;
  title: string;
  subtitle?: string;
  description: string;
  twoYearOutlook?: string;
  exampleCompanies?: string[];
  compRange?: string;
}

export interface OnboardingCareerQuiz {
  source: OnboardingCareerQuizSource;
  questions: OnboardingCareerQuizQuestion[];
  answers: OnboardingCareerQuizAnswer[];
  scores: Record<string, number>;
  pathwayPreview: OnboardingCareerPathPreview[];
  generatedAt: string;
}

export type OnboardingSearchIntent = "active" | "exploring";

export interface OnboardingAnswers {
  resume: OnboardingResume | null;
  goal: OnboardingGoal | null;
  networkSources: OnboardingNetworkSource[];
  manualContacts: OnboardingManualContact[];
  relationshipComfort: OnboardingRelationshipComfort;
  searchIntent: OnboardingSearchIntent | null;
  review: OnboardingReview | null;
  careerQuiz: OnboardingCareerQuiz | null;
  phone: OnboardingPhoneVerification | null;
}

export interface OnboardingDraft {
  id: string;
  userId: string;
  status: OnboardingDraftStatus;
  currentStep: OnboardingStepId;
  completedSteps: OnboardingStepId[];
  answers: OnboardingAnswers;
  revision: number;
  createdAt: string;
  updatedAt: string;
}

export interface OnboardingProfileSeed {
  resumeSignals: ResumeParsedSignal[];
  goalHeadline: string;
  targetLabels: string[];
  selectedSourceKinds: NetworkSourceKind[];
  connectedSourceKinds: NetworkSourceKind[];
  outreachComfort: OutreachComfort;
}

export interface OnboardingDashboardSeed {
  mapHeadline: string;
  primaryGoal: string;
  nextActions: string[];
  targetCount: number;
  networkInputCount: number;
  resumeSignalCount: number;
}
